/**
 * Live Owned Performance, shaped for the page's existing renderers.
 *
 * `posting-insights-data.ts` exposes every accessor as
 * `filterX(filters, rows = MOCK_ROWS)` — the dataset is a defaulted second
 * argument, not a closed-over constant. That was left open deliberately, and it
 * is the whole reason this file is an adapter rather than a rewrite: pass live
 * rows in and every chart, sort and heatmap keeps working untouched.
 *
 * **Do NOT pass these rows into `filterPosts` / `filterDaily` / `filterFollowers`.**
 * That is what the defaulted second argument invites, and it is a trap: those
 * helpers range-filter against `ANCHOR`, a hardcoded `2026-08-18T12:00:00Z` in
 * `posting-insights-data.ts`. Real posts published after that date fail
 * `inRange` and vanish — no error, no empty state, just a shorter list. It is
 * the same silent-empty failure that made every author watcher return zero for
 * weeks. Use these arrays directly; the server has already applied range,
 * platform and source.
 *
 * What the server does NOT filter is `handle` — it takes `said`, not a handle
 * string — so that one stays client-side.
 *
 * **These rows are `measurement_basis: "connected"`.** They must never be mixed
 * with anything from `/radar/performance/tracked`, which is `public`. On our own
 * accounts the public follower proxy ran 3.29x high on Instagram and 1.75x low
 * on Facebook against true reach — opposite directions, so no correction
 * reconciles them.
 */
import type {
  ApiOwnedDailyPoint,
  ApiOwnedPost,
  ApiPerformanceOwned,
} from '@/client/types/marketing/api';
import type {
  PostFormat,
  PostingInsightDaily,
  PostingInsightFollowers,
  PostingInsightMetrics,
  PostingInsightPost,
  PostingInsightSource,
  SocialPlatform,
} from '@/client/types/marketing';
import { bucketStart, HEAT_DAYS, HEAT_HOURS, type HeatCell, type TimeGrain } from './posting-insights-data';
import { utcDayKey } from './performance-range-storage';

const PLATFORMS: readonly SocialPlatform[] = [
  'x', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube', 'threads',
  'pinterest', 'reddit', 'bluesky', 'google_business', 'telegram', 'snapchat',
  'whatsapp', 'discord', 'other',
];

/** Unknown vendor platform -> 'other' rather than a cast. A bad cast surfaces
 *  as a missing icon and an unfilterable row; 'other' is at least true. */
const toPlatform = (raw: string | null | undefined): SocialPlatform =>
  PLATFORMS.includes(raw as SocialPlatform) ? (raw as SocialPlatform) : 'other';

/**
 * Absent metrics become 0 HERE and only here.
 *
 * The API omits keys it has no reading for — an Instagram image post reports no
 * `views`, and a 0 there would make "we do not know" and "nobody watched"
 * identical. `PostingInsightMetrics` requires every key, so the zero-fill has to
 * happen at the boundary. Everything upstream of this line keeps absence
 * absent; anything reading these numbers is looking at a sum, where a missing
 * addend and a zero addend are genuinely the same.
 */
const toMetrics = (m: ApiOwnedPost['metrics']): PostingInsightMetrics => ({
  likes: m?.likes ?? 0,
  comments: m?.comments ?? 0,
  shares: m?.shares ?? 0,
  saves: m?.saves ?? 0,
  clicks: m?.clicks ?? 0,
  views: m?.views ?? 0,
  follows: m?.follows ?? 0,
  impressions: m?.impressions ?? 0,
  reach: m?.reach ?? 0,
});

/** The vendor's vocabulary is `late` | `external`; anything else reads as
 *  natively published, which is what every owned post in the tenant is. */
const toSource = (raw: string | null | undefined): PostingInsightSource =>
  raw === 'late' ? 'late' : 'external';

const HTTP_URL = /^https?:\/\//i;

type LooseOwnedPost = ApiOwnedPost & {
  permalink?: string | null;
  permalink_url?: string | null;
  external_url?: string | null;
  post_url?: string | null;
};

function stripAt(handle: string | null | undefined): string {
  return (handle ?? '').replace(/^@+/, '').trim();
}

/** Prefer vendor permalinks; only invent URLs when the id shape is reliable. */
export function resolveOwnedPostUrl(post: LooseOwnedPost): string | null {
  const candidates = [
    post.url,
    post.permalink,
    post.permalink_url,
    post.external_url,
    post.post_url,
  ];
  for (const c of candidates) {
    if (typeof c === 'string') {
      const t = c.trim();
      if (HTTP_URL.test(t)) return t;
    }
  }

  const id = (post.external_post_id ?? '').trim();
  if (!id) return null;
  const platform = toPlatform(post.platform);
  const handle = stripAt(post.handle);

  switch (platform) {
    case 'x':
      return handle
        ? `https://x.com/${encodeURIComponent(handle)}/status/${encodeURIComponent(id)}`
        : `https://x.com/i/web/status/${encodeURIComponent(id)}`;
    case 'youtube':
      if (/^[\w-]{11}$/.test(id)) return `https://www.youtube.com/watch?v=${id}`;
      return null;
    case 'tiktok':
      return handle
        ? `https://www.tiktok.com/@${encodeURIComponent(handle)}/video/${encodeURIComponent(id)}`
        : null;
    case 'instagram':
      // Shortcodes work in /p/; raw Graph media ids (all digits) do not.
      if (/^[A-Za-z0-9_-]+$/.test(id) && !/^\d+$/.test(id)) {
        return `https://www.instagram.com/p/${encodeURIComponent(id)}/`;
      }
      return null;
    case 'facebook': {
      if (id.includes('_')) {
        const [pageId, postId] = id.split('_');
        if (pageId && postId) {
          return `https://www.facebook.com/${encodeURIComponent(pageId)}/posts/${encodeURIComponent(postId)}`;
        }
      }
      if (/^\d+$/.test(id)) return `https://www.facebook.com/${encodeURIComponent(id)}`;
      return null;
    }
    case 'linkedin': {
      const activity = id.replace(/^.*activity:/i, '');
      if (/^\d+$/.test(activity)) {
        return `https://www.linkedin.com/feed/update/urn:li:activity:${activity}`;
      }
      return null;
    }
    case 'threads':
      return handle
        ? `https://www.threads.net/@${encodeURIComponent(handle)}/post/${encodeURIComponent(id)}`
        : null;
    default:
      return null;
  }
}

export function livePosts(data: ApiPerformanceOwned | undefined): PostingInsightPost[] {
  return (data?.top_posts ?? []).map((post) => ({
    id: post.external_post_id || post.id,
    platform: toPlatform(post.platform),
    handle: post.handle ?? '—',
    source: toSource(post.source),
    caption: post.content ?? '',
    // The page parses this with Date.parse. A post with no timestamp would
    // become NaN and sort to one end silently, so it is dropped from range
    // filters by being given the epoch rather than an unparseable string.
    publishedAt: post.published_at ?? new Date(0).toISOString(),
    mediaTone: 'neutral',
    mediaLabel: toSource(post.source) === 'late' ? 'Studio' : 'Native',
    metrics: toMetrics(post.metrics),
    url: resolveOwnedPostUrl(post),
    engagement: post.engagement ?? 0,
    format: toFormat(post.media_type),
  }));
}

const FORMATS: readonly PostFormat[] = ['video', 'image', 'carousel', 'text'];

function toFormat(raw: string | null | undefined): PostFormat | null {
  const v = (raw ?? '').toLowerCase();
  return (FORMATS as readonly string[]).includes(v) ? (v as PostFormat) : null;
}

export interface FormatRow {
  format: PostFormat;
  posts: number;
  er: number;
  /** Relative difference vs the classified average, in percent. */
  vsAvg: number;
}

/**
 * Engagement rate per content format, pooled the same way as the platform
 * table (sum the metrics, then rate) so the two never disagree. Posts with no
 * known format are counted in `unknown`, not guessed into a bucket.
 */
export function formatBreakdown(
  posts: PostingInsightPost[],
  rate: (m: PostingInsightPost['metrics']) => number,
  sum: (p: PostingInsightPost[]) => PostingInsightPost['metrics'],
): { rows: FormatRow[]; avg: number; unknown: number } {
  const known = posts.filter((p) => p.format);
  const avg = known.length > 0 ? rate(sum(known)) : 0;
  const rows = FORMATS.map((format) => {
    const slice = known.filter((p) => p.format === format);
    const er = slice.length > 0 ? rate(sum(slice)) : 0;
    return { format, posts: slice.length, er, vsAvg: avg > 0 ? ((er - avg) / avg) * 100 : 0 };
  })
    .filter((r) => r.posts > 0)
    .sort((a, b) => b.er - a.er);
  return { rows, avg, unknown: posts.length - known.length };
}

function optionalMetric(v: number | null | undefined): number | undefined {
  return v == null || !Number.isFinite(v) ? undefined : v;
}

function toDailyRow(row: ApiOwnedDailyPoint): PostingInsightDaily {
  return {
    date: utcDayKey(row.date),
    platform: toPlatform(row.platform),
    posts: optionalMetric(row.posts) ?? 0,
    likes: optionalMetric(row.likes),
    comments: optionalMetric(row.comments),
    shares: optionalMetric(row.shares),
    saves: optionalMetric(row.saves),
    views: optionalMetric(row.views),
    impressions: optionalMetric(row.impressions),
    reach: optionalMetric(row.reach),
    clicks: optionalMetric(row.clicks),
  };
}

export function liveDaily(data: ApiPerformanceOwned | undefined): PostingInsightDaily[] {
  return (data?.series ?? []).map(toDailyRow);
}

/** Posts-derived cadence series. Empty until the owned payload grows `published_series`. */
export function livePublishedDaily(
  data: ApiPerformanceOwned | undefined,
): PostingInsightDaily[] {
  return (data?.published_series ?? []).map(toDailyRow);
}

export const ENGAGEMENT_METRIC_KEYS = [
  'likes',
  'comments',
  'shares',
  'saves',
  'views',
  'impressions',
  'reach',
  'clicks',
] as const;

export type EngagementMetricKey = (typeof ENGAGEMENT_METRIC_KEYS)[number];

export type EngagementOverTimeRow = {
  /** Bucket start, YYYY-MM-DD. */
  date: string;
} & Record<EngagementMetricKey, number | null>;

function pickMetric(row: ApiOwnedDailyPoint, key: EngagementMetricKey): number | null {
  const v = row[key];
  return v == null || !Number.isFinite(v) ? null : v;
}

function addNullable(a: number | null, b: number | null): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

/**
 * Vendor daily-metrics for Engagement over time. Nulls stay null so an omitted
 * key does not draw a zero line. Week / month grain is a client roll of those days.
 */
export function liveEngagementOverTime(
  data: ApiPerformanceOwned | undefined,
  grain: TimeGrain,
  platforms?: ReadonlySet<SocialPlatform>,
): EngagementOverTimeRow[] {
  const byBucket = new Map<string, EngagementOverTimeRow>();
  for (const r of data?.series ?? []) {
    const iso = utcDayKey(r.date);
    if (!iso) continue;
    if (platforms && !platforms.has(toPlatform(r.platform))) continue;
    const bucket = bucketStart(iso, grain);
    const existing = byBucket.get(bucket) ?? {
      date: bucket,
      likes: null,
      comments: null,
      shares: null,
      saves: null,
      views: null,
      impressions: null,
      reach: null,
      clicks: null,
    };
    for (const key of ENGAGEMENT_METRIC_KEYS) {
      existing[key] = addNullable(existing[key], pickMetric(r, key));
    }
    byBucket.set(bucket, existing);
  }
  return [...byBucket.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/** Running totals of `rows` (oldest first). A metric stays null until its first value. */
export function cumulateEngagement(rows: EngagementOverTimeRow[]): EngagementOverTimeRow[] {
  const run: Partial<Record<EngagementMetricKey, number | null>> = {};
  return rows.map((r) => {
    const out = { ...r };
    for (const key of ENGAGEMENT_METRIC_KEYS) {
      run[key] = addNullable(run[key] ?? null, r[key]);
      out[key] = run[key] ?? null;
    }
    return out;
  });
}

/** `daily_points === 0` means the daily-metrics beat has not landed. Absent key = old payload. */
export const isDailyUnsynced = (data: ApiPerformanceOwned | undefined): boolean =>
  data?.store.daily_points === 0;

export function liveFollowers(
  data: ApiPerformanceOwned | undefined,
): PostingInsightFollowers[] {
  return (data?.followers ?? []).map((row) => ({
    date: utcDayKey(row.date),
    platform: toPlatform(row.platform),
    followers: row.followers ?? 0,
  }));
}

/** Vendor decay is labelled buckets in `order`. Do not invent hours. */
export function liveContentDecay(data: ApiPerformanceOwned | undefined) {
  return (data?.content_decay ?? [])
    .map((pt, i) => {
      if (pt.pct == null || !Number.isFinite(pt.pct)) return null;
      const order = pt.order ?? pt.hour ?? i;
      const bucket =
        pt.label ??
        (pt.hour != null
          ? pt.hour < 24
            ? `${pt.hour}h`
            : `${Math.round(pt.hour / 24)}d`
          : String(order));
      return {
        order,
        bucket,
        pct: Math.round(asPercent(pt.pct)),
        posts: pt.posts,
      };
    })
    .filter((row): row is { order: number; bucket: string; pct: number; posts: number | undefined } => row != null)
    .sort((a, b) => a.order - b.order);
}

/**
 * Cadence scatter. `posts_per_week` is x. Rows without it are the old weekly
 * series — skip them rather than drawing a timeline.
 */
export function livePostingFrequency(data: ApiPerformanceOwned | undefined) {
  return (data?.posting_frequency ?? [])
    .filter(
      (row) =>
        row.posts_per_week != null &&
        Number.isFinite(row.posts_per_week) &&
        row.engagement_rate != null &&
        Number.isFinite(row.engagement_rate),
    )
    .map((row) => ({
      platform: toPlatform(row.platform),
      posts_per_week: row.posts_per_week as number,
      // Already a percent: Facebook's 3/wk bucket is 0.99, which asPercent
      // would inflate to 99%.
      er: row.engagement_rate as number,
      avg_engagement: row.avg_engagement ?? null,
      weeks: row.weeks ?? null,
    }))
    // Ordered by x so each platform's line joins its cadences left to right.
    .sort((a, b) => a.posts_per_week - b.posts_per_week);
}

/** Highest-ER cadence per platform, best platform first. */
export function bestCadence(rows: ReturnType<typeof livePostingFrequency>) {
  const best = new Map<SocialPlatform, (typeof rows)[number]>();
  for (const r of rows) {
    const cur = best.get(r.platform);
    if (!cur || r.er > cur.er) best.set(r.platform, r);
  }
  return [...best.values()].sort((a, b) => b.er - a.er);
}

function asPercent(raw: number): number {
  return Math.abs(raw) <= 1 ? raw * 100 : raw;
}

/**
 * Has anything ever synced?
 *
 * `store.posts === 0` means the sync has never run. Empty arrays with
 * `store.posts > 0` mean a genuinely quiet window. They render identically
 * without this distinction and mean opposite things — one is a broken pipeline,
 * the other is a slow week.
 */
export const isUnsynced = (data: ApiPerformanceOwned | undefined): boolean =>
  !data || (data.store?.posts ?? 0) === 0;

/**
 * True when a follower line changes because an ACCOUNT was added, not because
 * anyone followed us. Worth annotating rather than drawing as growth.
 */
export const hasAccountStep = (data: ApiPerformanceOwned | undefined): boolean => {
  const counts = (data?.followers ?? []).map((f) => f.accounts ?? 0);
  return new Set(counts.filter(Boolean)).size > 1;
};

/**
 * Vendor best-time → the page's Mon×3h heatmap grid.
 *
 * Prefer the vendor derivation when it has any signal. When the derived sync
 * has not filled `owned_derived` yet (common after a posts-only sync), fall
 * back to an estimate from posts in the current window so the panel is not a
 * blank green grid. The fallback is labeled in the UI — do not treat it as the
 * vendor recommendation.
 *
 * `day` is 0=Mon … 6=Sun (matches HEAT_DAYS); `hour` is 0–23 UTC, one cell each.
 * String weekday names still map. Absent slots stay `null`, not 0.
 */
export type HeatSource = 'vendor' | 'posts' | 'empty';

export interface LiveHeat {
  cells: HeatCell[];
  source: HeatSource;
}

const DAY_NAME_TO_IDX: Record<string, number> = {
  mon: 0, monday: 0,
  tue: 1, tues: 1, tuesday: 1,
  wed: 2, wednesday: 2,
  thu: 3, thur: 3, thurs: 3, thursday: 3,
  fri: 4, friday: 4,
  sat: 5, saturday: 5,
  sun: 6, sunday: 6,
};

function normalizeDayIdx(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = Math.trunc(raw);
    if (n >= 0 && n <= 6) return n;
    return null;
  }
  if (typeof raw === 'string') {
    const key = raw.trim().toLowerCase();
    if (key in DAY_NAME_TO_IDX) return DAY_NAME_TO_IDX[key];
    const n = Number(key);
    if (Number.isFinite(n)) return normalizeDayIdx(n);
  }
  return null;
}

function emptyHeatGrid(): HeatCell[] {
  const cells: HeatCell[] = [];
  for (const day of HEAT_DAYS) {
    for (const hour of HEAT_HOURS) cells.push({ day, hour, value: null });
  }
  return cells;
}

/** Honest post-time estimate — no mock seed. */
export function heatFromPosts(posts: PostingInsightPost[]): HeatCell[] {
  const scores = new Map<string, number>();
  for (const p of posts) {
    const t = Date.parse(p.publishedAt);
    if (!Number.isFinite(t) || t <= 0) continue;
    const d = new Date(t);
    const dayIdx = (d.getUTCDay() + 6) % 7; // Mon=0
    const hour = d.getUTCHours();
    const key = `${dayIdx}:${hour}`;
    const eng =
      p.engagement ??
      p.metrics.likes + p.metrics.comments + p.metrics.shares + p.metrics.saves;
    scores.set(key, (scores.get(key) ?? 0) + Math.max(eng, 1));
  }
  const cells: HeatCell[] = [];
  for (let di = 0; di < HEAT_DAYS.length; di++) {
    for (const hour of HEAT_HOURS) {
      cells.push({
        day: HEAT_DAYS[di],
        hour,
        value: scores.get(`${di}:${hour}`) ?? null,
      });
    }
  }
  return cells;
}

function heatHasSignal(cells: HeatCell[]): boolean {
  return cells.some((c) => c.value != null);
}

export function liveHeat(
  data: ApiPerformanceOwned | undefined,
  posts: PostingInsightPost[] = [],
): LiveHeat {
  const cells = emptyHeatGrid();
  const points = data?.best_time ?? [];
  for (const pt of points) {
    const dayIdx = normalizeDayIdx(pt.day);
    if (dayIdx == null) continue;
    const hourRaw = typeof pt.hour === 'number' ? pt.hour : Number(pt.hour);
    if (!Number.isFinite(hourRaw)) continue;
    const hour = Math.trunc(hourRaw);
    if (pt.score == null || !Number.isFinite(pt.score)) continue;
    const cell = cells.find((c) => c.day === HEAT_DAYS[dayIdx] && c.hour === hour);
    if (!cell) continue;
    cell.value = pt.score;
    cell.posts = typeof pt.posts === 'number' ? pt.posts : undefined;
  }

  if (heatHasSignal(cells)) return { cells, source: 'vendor' };

  const fromPosts = heatFromPosts(posts);
  if (heatHasSignal(fromPosts)) return { cells: fromPosts, source: 'posts' };

  return { cells: emptyHeatGrid(), source: 'empty' };
}
